import { onMounted, onUnmounted, watch, type WatchSource } from "vue";
import { Facade } from "@/drawing";
import type { WorldBbox } from "@/utils/plan-analysis/types";

/** Rectangle projeté à l'écran (pixels CSS du conteneur du canvas). */
export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Projette une emprise monde en rectangle écran, `padding` px de marge
 * autour. Le backing store du canvas est en pixels CSS (`resize()` du viewer
 * recopie `clientWidth`) : `worldToScreen` est directement utilisable.
 */
export function projectWorldBbox(bbox: WorldBbox, padding = 0): ScreenRect {
  const a = Facade.view.worldToScreen({ x: bbox.minX, y: bbox.minY });
  const b = Facade.view.worldToScreen({ x: bbox.maxX, y: bbox.maxY });
  return {
    left: Math.min(a.x, b.x) - padding,
    top: Math.min(a.y, b.y) - padding,
    width: Math.abs(b.x - a.x) + 2 * padding,
    height: Math.abs(b.y - a.y) + 2 * padding,
  };
}

/**
 * Tient une surcouche de cadres calée sur le plan : `compute` est rejoué à
 * chaque changement de vue (pan/zoom — au plus une fois par frame
 * d'affichage, les pans génèrent des rafales) et à chaque changement des
 * `sources`. Rien n'est dessiné dans le moteur : la surcouche est du pur
 * HTML au-dessus du canvas.
 */
export function useViewSyncedCompute(
  compute: () => void,
  sources: WatchSource[],
): void {
  let rafId = 0;
  let unsubscribe: (() => void) | null = null;

  const schedule = (): void => {
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      compute();
    });
  };

  onMounted(() => {
    if (Facade.isInitialized) {
      unsubscribe = Facade.view.onViewChanged(schedule);
    }
    compute();
  });

  watch(sources, schedule);

  onUnmounted(() => {
    unsubscribe?.();
    if (rafId) cancelAnimationFrame(rafId);
  });
}
